import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { TOKEN_KEY, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [branches, setBranches] = useState([]);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      }
    }
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const { data } = await api.get("/branches");
      setBranches(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setUser(false); return; }
    api.get("/auth/me")
      .then(({ data }) => { setUser(data); loadBranches(); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(false); });
  }, [loadBranches]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState !== "hidden") refreshUser();
    };
    const timer = window.setInterval(refreshUser, 60_000);
    window.addEventListener("focus", refreshUser);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshUser);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [refreshUser]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    await loadBranches();
    return data.user;
  };

  const googleLogin = useCallback(async (credential) => {
    const { data } = await api.post("/auth/google", { credential });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    await loadBranches();
    return data.user;
  }, [loadBranches]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const hasRole = (...roles) => user && roles.includes(user.role);
  const can = useCallback((permission) => Boolean(user && user.permissions?.includes(permission)), [user]);
  const canManage = () => hasRole("super_admin", "management");
  const isFullAccess = () => hasRole("super_admin", "management", "director");

  return (
    <AuthContext.Provider value={{ user, branches, loadBranches, refreshUser, login, googleLogin, logout, hasRole, can, canManage, isFullAccess, apiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
